
import React from "react";
import { useRef, useState, useEffect } from "react";
import { FiTrash2, FiShield, FiPlus } from "react-icons/fi";




export function useDragWindowLike(initial = { x: 0, y: 0 }, opts = {}) {
    const {
        onMove: onMoveCb,
        onDragStart,
        onDragEnd,
        parentRef,     // offset parent (your WORLD layer)
        boundsRef,     // clamp bounds (usually same as parentRef)
        zoomRef,       // current scale ref (e.g., 0.5..2)
    } = opts;

    const [pos, setPos] = useState(initial);
    const posRef = useRef(initial);
    const dragRef = useRef(null); // { dx, dy, parentLeft, parentTop, scale, pointerId }

    useEffect(() => { posRef.current = pos; }, [pos]);

    function beginDrag(e) {
        e.preventDefault();
        e.stopPropagation();

        const cardEl = e.currentTarget.parentElement; // header -> card
        const parentEl = parentRef?.current || cardEl?.offsetParent || document.body;
        const parentRect = parentEl.getBoundingClientRect();
        const scale = zoomRef?.current || 1;

        // pointer in WORLD coords
        const startWorldX = (e.clientX - parentRect.left) / scale;
        const startWorldY = (e.clientY - parentRect.top) / scale;

        // offset from current pos (WORLD)
        const dx = startWorldX - posRef.current.x;
        const dy = startWorldY - posRef.current.y;

        dragRef.current = {
            parentLeft: parentRect.left,
            parentTop: parentRect.top,
            dx,
            dy,
            scale,
            pointerId: e.pointerId,
            cardEl,
        };

        document.body.style.userSelect = "none";
        e.currentTarget.setPointerCapture?.(e.pointerId);

        onDragStart && onDragStart();

        const onPointerMove = (ev) => {
            if (!dragRef.current) return;
            const { parentLeft, parentTop, dx, dy, scale, cardEl } = dragRef.current;

            // convert to WORLD coords every move
            const worldX = (ev.clientX - parentLeft) / scale;
            const worldY = (ev.clientY - parentTop) / scale;

            let x = worldX - dx;
            let y = worldY - dy;

            // clamp to WORLD bounds (unscaled)
            if (boundsRef?.current) {
                const bw = boundsRef.current.clientWidth;
                const bh = boundsRef.current.clientHeight;
                const w = cardEl.offsetWidth;
                const h = cardEl.offsetHeight;
                x = Math.min(Math.max(x, 0), bw - w);
                y = Math.min(Math.max(y, 0), bh - h);
            }

            setPos({ x, y });
            onMoveCb && onMoveCb({ x, y });
        };

        const endDrag = () => {
            dragRef.current = null;
            document.body.style.userSelect = "";
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", endDrag);
            window.removeEventListener("pointercancel", endDrag);
            onDragEnd && onDragEnd();
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", endDrag, { once: true });
        window.addEventListener("pointercancel", endDrag, { once: true });
    }

    useEffect(() => {
        // hard cleanup if component unmounts mid-drag
        return () => {
            dragRef.current = null;
            document.body.style.userSelect = "";
            window.removeEventListener("pointermove", () => { });
            window.removeEventListener("pointerup", () => { });
            window.removeEventListener("pointercancel", () => { });
        };
    }, []);

    return { pos, setPos, beginDrag };
}



export default function DraggableThreatCard({
    threat,
    threatControls,
    onRemoveThreat,
    onUpdateThreatText,
    onAddThreatControl,
    onRemoveThreatControl,
    onUpdateThreatControlText,
    onThreatControlIconClick,
    allowDrop,
    handleDropOnThreat,
    onHoverChange,
    initialPos,
    ownerId,
    // NEW: give us the canvas wrapper and a callback
    onDragStart,
    onDragEnd,
    canvasRef,
    scaledLayerRef,
    zoomRef,
    reportAnchor,               // (id, {x,y}) => void  relative to canvas
}) {
    const cardRef = useRef(null);

    // when we move, compute right-middle of the card RELATIVE TO canvas
    const { pos, beginDrag } = useDragWindowLike(initialPos, {
        onMove: ({ x, y }) => reportImmediate(x, y),   // ← use live coords from the hook
        onDragStart,
        onDragEnd,
        parentRef: canvasRef,
        boundsRef: null,
        zoomRef,
    });




    function reportImmediate(x, y) {
        if (!cardRef.current) return;
        const w = cardRef.current.offsetWidth;   // unscaled world size
        const h = cardRef.current.offsetHeight;
        const anchorX = x + w;                   // RIGHT-middle for threats
        const anchorY = y + h / 2;
        reportAnchor && reportAnchor(threat.ClientID, { x: anchorX, y: anchorY });
    }

    function handleHeaderPointerDown(e) {
        // left button only
        if (e.button !== 0) return;
        // don't start drag when interacting with inputs/buttons/etc.
        if (e.target.closest("input, textarea, button, [data-no-drag]")) return;
        beginDrag(e);
    }

    function reportNow() {
        if (!cardRef.current || !canvasRef?.current) return;
        const w = cardRef.current.offsetWidth;   // unscaled
        const h = cardRef.current.offsetHeight;  // unscaled
        // pos.x/pos.y are already world coords
        const isThreat = !!threat; // or pass a flag
        const x = isThreat ? (pos.x + w) : pos.x;     // RIGHT middle for threats, LEFT middle for consequences
        const y = pos.y + h / 2;
        reportAnchor && reportAnchor(threat.ClientID, { x, y });
    }

    // report on mount + whenever pos changes (covers both drag + programmatic moves)
    useEffect(() => {
        if (!cardRef.current) return;
        reportImmediate(pos.x, pos.y);
    }, [pos.x, pos.y]);

    useEffect(() => {
        const onResize = () => reportImmediate(pos.x, pos.y);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return (
        <div
            ref={cardRef}
            data-nopan
            className="absolute z-20 w-[220px] bg-white shadow-sm border rounded-md"
            style={{
                left: pos.x,
                top: pos.y,
                boxSizing: "border-box",
                touchAction: "none",
            }}
            onDragOver={allowDrop}
            onDrop={(e) => handleDropOnThreat(e, threat.ClientID)}
            onMouseEnter={() => onHoverChange && onHoverChange(threat.ClientID)}
            onMouseLeave={() => onHoverChange && onHoverChange(null)}
        >
            {/* HEADER */}
            <div
                className="flex items-center justify-between px-3 py-2 rounded-t-md border-b select-none bg-[#3B3B3B]"
                onPointerDown={(e) => {
                    e.stopPropagation();
                    handleHeaderPointerDown(e);
                }}
            >
                <span className="font-semibold text-white text-sm">Threat</span>

                <button
                    type="button"
                    onClick={() => onRemoveThreat(threat.ClientID)}
                    title="Remove threat"
                    aria-label="Remove threat"
                    className="text-white hover:text-red-600 text-sm px-2 py-1 rounded hover:bg-gray-200"
                    draggable={false}
                >
                    <FiTrash2 size={16} />
                </button>
            </div>

            {/* BODY */}
            <div className="p-4 min-h-[80px] flex items-start">
                <textarea
                    value={threat.Text}
                    onChange={(e) => onUpdateThreatText(threat.ClientID, e.target.value)}
                    placeholder="Threat description"
                    className="w-full bg-transparent outline-none border-none focus:ring-0 text-sm text-gray-900 resize-none overflow-hidden leading-snug font-normal"
                    rows={2}
                    draggable={false}
                />
            </div>
        </div>
    );

}